import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, TextField,
  IconButton, Typography, FormControlLabel, Checkbox, Alert, CircularProgress, Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AddIcon from '@mui/icons-material/Add';
import { taskStatusesAPI } from '../../services/api';

/**
 * Manage a project's board columns (statuses): add / rename / reorder / delete /
 * mark as "done". Internal only (backend blocks clients). Each action persists
 * immediately; onChanged() lets the caller reload the board.
 */
function ColumnManager({ open, onClose, projectId, onChanged }) {
  const [cols, setCols] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newName, setNewName] = useState('');

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await taskStatusesAPI.getAll(projectId);
      setCols((res.data || []).slice().sort((a, b) => a.order - b.order));
    } catch (err) {
      setError('Spalten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { if (open) { setError(null); load(); } }, [open, load]);

  const notify = () => { if (onChanged) onChanged(); };

  const rename = async (col, name) => {
    if (!name.trim() || name === col.name) return;
    try {
      await taskStatusesAPI.update(col.id, { name: name.trim() });
      await load(); notify();
    } catch (err) { setError(err.response?.data?.error || 'Umbenennen fehlgeschlagen'); }
  };

  const toggleDone = async (col, value) => {
    try {
      await taskStatusesAPI.update(col.id, { is_done_status: value });
      await load(); notify();
    } catch (err) { setError('Änderung fehlgeschlagen'); }
  };

  const toggleBacklog = async (col, value) => {
    try {
      await taskStatusesAPI.update(col.id, { is_backlog: value });
      await load(); notify();
    } catch (err) { setError('Änderung fehlgeschlagen'); }
  };

  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= cols.length) return;
    const reordered = cols.slice();
    const [item] = reordered.splice(index, 1);
    reordered.splice(target, 0, item);
    setCols(reordered); // optimistic
    try {
      await taskStatusesAPI.reorder(reordered.map((c, i) => ({ id: c.id, order: i })));
      await load(); notify();
    } catch (err) { setError('Sortierung fehlgeschlagen'); load(); }
  };

  const remove = async (col) => {
    setError(null);
    try {
      await taskStatusesAPI.delete(col.id);
      await load(); notify();
    } catch (err) {
      setError(err.response?.data?.error || 'Spalte konnte nicht gelöscht werden');
    }
  };

  const add = async () => {
    if (!newName.trim()) return;
    try {
      await taskStatusesAPI.create({ name: newName.trim(), project_id: projectId });
      setNewName('');
      await load(); notify();
    } catch (err) { setError(err.response?.data?.error || 'Spalte konnte nicht angelegt werden'); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight={600}>Spalten verwalten</Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {cols.map((col, i) => (
              <Box key={col.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: col.color || '#999', flexShrink: 0 }} />
                <TextField
                  size="small" defaultValue={col.name} fullWidth
                  onBlur={(e) => rename(col, e.target.value)}
                />
                <FormControlLabel
                  sx={{ m: 0, whiteSpace: 'nowrap' }}
                  control={<Checkbox size="small" checked={!!col.is_done_status}
                    onChange={(e) => toggleDone(col, e.target.checked)} />}
                  label={<Typography variant="caption">erledigt</Typography>}
                />
                <FormControlLabel
                  sx={{ m: 0, whiteSpace: 'nowrap' }}
                  control={<Checkbox size="small" checked={!!col.is_backlog}
                    onChange={(e) => toggleBacklog(col, e.target.checked)} />}
                  label={<Typography variant="caption">Backlog</Typography>}
                />
                <IconButton size="small" disabled={i === 0} onClick={() => move(i, -1)}><ArrowUpwardIcon fontSize="small" /></IconButton>
                <IconButton size="small" disabled={i === cols.length - 1} onClick={() => move(i, 1)}><ArrowDownwardIcon fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => remove(col)} title="Löschen"><DeleteIcon fontSize="small" color="error" /></IconButton>
              </Box>
            ))}

            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField size="small" fullWidth placeholder="Neue Spalte…" value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') add(); }} />
              <Button variant="outlined" startIcon={<AddIcon />} onClick={add} disabled={!newName.trim()}>
                Hinzufügen
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              Spalten mit Tasks lassen sich nicht löschen – Tasks vorher verschieben. „erledigt" markiert
              die Abschluss-Spalte. „Backlog" zeigt die Spalte als Liste unterhalb des Boards; neue Tasks
              (auch per API) landen automatisch dort.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="contained">Fertig</Button>
      </DialogActions>
    </Dialog>
  );
}

export default ColumnManager;
